import React, { useMemo } from "react";
import { MATERIAL_REQUEST_STATUS } from "../../../../data/enums";
import { MaterialRequestFilterType, valueToText } from "../../../../configs/filterTypes";

export interface StatusProgressProps {
    status?: MATERIAL_REQUEST_STATUS | string | null;
    className?: string;
}

const StatusProgress: React.FC<StatusProgressProps> = ({ status, className }) => {
    const materialRequestStatuses: MATERIAL_REQUEST_STATUS[] = useMemo(
        () => [
            MATERIAL_REQUEST_STATUS.OPEN,
            MATERIAL_REQUEST_STATUS.EXPORTING,
            MATERIAL_REQUEST_STATUS.COMPLETED,
        ],
        []
    );

    const normalizedStatus = (status || "").toString() as MATERIAL_REQUEST_STATUS;
    const isCancelled = normalizedStatus === MATERIAL_REQUEST_STATUS.CANCELLED;
    const isCompleted = normalizedStatus === MATERIAL_REQUEST_STATUS.COMPLETED;

    const currentIndex = useMemo(
        () => materialRequestStatuses.indexOf(normalizedStatus),
        [normalizedStatus, materialRequestStatuses]
    );

    const labels = useMemo(
        () =>
            materialRequestStatuses.map(
                (s) => valueToText(MaterialRequestFilterType, "status", s) || s
            ),
        [materialRequestStatuses]
    );

    const getLabelClass = (i: number) => {
        if (isCancelled) return "tw-text-red-600 tw-font-semibold";
        if (currentIndex < 0) return "";
        if (i < currentIndex) return "tw-text-black tw-font-semibold";
        if (i === currentIndex) return "tw-text-black tw-font-semibold";
        return "tw-text-gray-600";
    };

    const getSegmentClass = (i: number) => {
        if (isCompleted) return "tw-bg-emerald-600";
        if (isCancelled) return "tw-bg-red-600";
        if (currentIndex < 0) return "tw-bg-secondary";
        if (i < currentIndex) return "tw-bg-emerald-600";
        if (i === currentIndex) return "tw-bg-orange-500";
        return "tw-bg-secondary";
    };

    return (
        <div className={className} style={{ width: "100%" }}>
            {/* Segmented progress track with labels under each step */}
            <div className="tw-w-full tw-flex tw-gap-1">
                {materialRequestStatuses.map((_, i) => (
                    <div key={i} className="tw-flex-1 tw-flex tw-flex-col tw-items-stretch">
                        <div className={`tw-h-2 tw-rounded ${getSegmentClass(i)}`} aria-hidden />
                        <div className="tw-text-[12px] tw-mt-2 tw-text-center">
                            <span className={getLabelClass(i)}>{labels[i]}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default StatusProgress;
